import React, { useState, useEffect } from 'react';
import { nGram } from 'n-gram';

const ngramTypeLabels = {
  uni: "Unigrams",
  bi: "Bigrams",
  tri: "Trigrams",
  quad: "Quadrigrams"
};

const NGramDisplay = ({ input, onNGramClicked }) => {
  const [minFrequency, setMinFrequency] = useState('');
  const [debouncedMinFrequency, setDebouncedMinFrequency] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinFrequency(minFrequency);
    }, 300);
    return () => clearTimeout(handler);
  }, [minFrequency]);

  if (!input || input.type === 'none' || !input.data) {
    return <p style={{color: '#777', textAlign: 'center', marginTop: '20px' }}>No N-grams to display based on current selection.</p>;
  }

  let title = 'N-grams';
  let processable = false;
  let displayMode = input.type;

  let unigramsToRender = [], bigramsToRender = [], trigramsToRender = [], quadrigramsToRender = [];

  const calculateFrequencies = (ngramArrayList) => {
    const freqMap = new Map();
    for (const ngramArray of ngramArrayList) {
      const ngramString = ngramArray.join(' ');
      freqMap.set(ngramString, (freqMap.get(ngramString) || 0) + 1);
    }
    return Array.from(freqMap, ([text, count]) => ({ text, count }))
                .sort((a, b) => b.count - a.count);
  };

  if (input.type === 'single' && typeof input.data === 'string' && input.data.trim() !== '') {
    const textToProcess = input.data.trim();
    const tokens = textToProcess.split(/\s+/);
    if (tokens.length > 0) {
      unigramsToRender = nGram(1)(tokens).map(g => g.join(' '));
      bigramsToRender = tokens.length >= 2 ? nGram(2)(tokens).map(g => g.join(' ')) : [];
      trigramsToRender = tokens.length >= 3 ? nGram(3)(tokens).map(g => g.join(' ')) : [];
      quadrigramsToRender = tokens.length >= 4 ? nGram(4)(tokens).map(g => g.join(' ')) : [];
      title = `N-grams for: "${input.data}" (Normalized)`;
      if (input.data.length > 50) title = `N-grams for Selected Keyword (Normalized)`; // Use input.data for original text snippet
      processable = true;
    }
  } else if (input.type === 'multiple' && Array.isArray(input.data) && input.data.length > 0) {
    const allUnigramsRaw = [], allBigramsRaw = [], allTrigramsRaw = [], allQuadrigramsRaw = [];
    input.data.forEach(text => {
      if (typeof text === 'string' && text.trim() !== '') {
        const tokens = text.trim().split(/\s+/);
        if (tokens.length > 0) {
          allUnigramsRaw.push(...nGram(1)(tokens));
          if (tokens.length >= 2) allBigramsRaw.push(...nGram(2)(tokens));
          if (tokens.length >= 3) allTrigramsRaw.push(...nGram(3)(tokens));
          if (tokens.length >= 4) allQuadrigramsRaw.push(...nGram(4)(tokens));
          processable = true;
        }
      }
    });

    if (processable) {
      const parsedMinFreq = debouncedMinFrequency && !isNaN(parseInt(debouncedMinFrequency, 10)) && parseInt(debouncedMinFrequency, 10) > 0
                              ? parseInt(debouncedMinFrequency, 10) : 0;

      const freqUni = calculateFrequencies(allUnigramsRaw);
      const freqBi = calculateFrequencies(allBigramsRaw);
      const freqTri = calculateFrequencies(allTrigramsRaw);
      const freqQuad = calculateFrequencies(allQuadrigramsRaw);

      unigramsToRender = parsedMinFreq > 0 ? freqUni.filter(item => item.count >= parsedMinFreq) : freqUni;
      bigramsToRender = parsedMinFreq > 0 ? freqBi.filter(item => item.count >= parsedMinFreq) : freqBi;
      trigramsToRender = parsedMinFreq > 0 ? freqTri.filter(item => item.count >= parsedMinFreq) : freqTri;
      quadrigramsToRender = parsedMinFreq > 0 ? freqQuad.filter(item => item.count >= parsedMinFreq) : freqQuad;

      title = `Common N-grams from ${input.data.length} Displayed Keyword(s)`;
    }
  }

  if (!processable) {
    return <p style={{color: '#777', textAlign: 'center', marginTop: '20px' }}>Not enough data or invalid input for N-gram analysis.</p>;
  }

  const sectionBaseStyle = { marginTop: '10px', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f9f9f9' };
  const listStyle = { listStyleType: 'none', paddingLeft: '0', display: 'flex', flexWrap: 'wrap' };
  const itemStyle = {
    padding: '4px 8px', margin: '4px', border: '1px solid #007bff', borderRadius: '4px',
    backgroundColor: '#e7f3ff', color: '#004085', cursor: 'pointer',
    fontSize: '0.9em', textAlign: 'center',
  };
  const navLinkStyle = { marginRight: '15px', textDecoration: 'none', color: '#007bff', fontWeight: '500' };
  const navStyle = { marginBottom: '15px', borderBottom: '1px solid #ccc', paddingBottom: '10px', display: 'flex', justifyContent: 'center' };


  const renderNGramList = (items, type, typeLabelKey) => {
    const count = items.length;
    const titleText = displayMode === 'multiple'
      ? `Common ${ngramTypeLabels[typeLabelKey]} (${count})`
      : `${ngramTypeLabels[typeLabelKey]} (${count})`;

    if (count === 0) return <div id={`ngram-section-${typeLabelKey}`} style={sectionBaseStyle}><h5>{titleText}</h5><p style={{fontSize: '0.9em', color: '#555'}}>No {ngramTypeLabels[typeLabelKey].toLowerCase()} meet the criteria.</p></div>;

    const isFrequencyList = (displayMode === 'multiple');

    return (
      <div id={`ngram-section-${typeLabelKey}`} style={sectionBaseStyle}>
        <h5>{titleText}</h5>
        <ul style={listStyle}>
          {items.map((item, index) => {
            const nGramString = isFrequencyList ? item.text : item;
            const displayString = isFrequencyList ? `${item.text} (${item.count})` : item;
            const key = `${typeLabelKey}-${index}-${nGramString}-${isFrequencyList && item.count ? item.count : ''}`;
            return (
              <li key={key} style={itemStyle}
                onClick={() => onNGramClicked && onNGramClicked(nGramString)}
                role="button" tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') onNGramClicked && onNGramClicked(nGramString)}}
              >
                {displayString}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div style={{ marginTop: '10px', padding: '10px', borderTop: '1px solid #ddd' }}>
      <h4>{title}</h4>

      {input.type === 'multiple' && (
        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
          <label htmlFor="minNgramFreq" style={{ marginRight: '5px', fontSize: '0.9em' }}>Min Frequency:</label>
          <input
            type="number" id="minNgramFreq" placeholder="e.g., 2"
            value={minFrequency} onChange={(e) => setMinFrequency(e.target.value)}
            style={{ width: '80px', padding: '4px', fontSize: '0.9em', border: '1px solid #ccc', borderRadius: '3px' }}
          />
        </div>
      )}

      <nav style={navStyle}>
        <a href="#ngram-section-uni" style={navLinkStyle}>Unigrams</a>
        <a href="#ngram-section-bi" style={navLinkStyle}>Bigrams</a>
        <a href="#ngram-section-tri" style={navLinkStyle}>Trigrams</a>
        <a href="#ngram-section-quad" style={{...navLinkStyle, marginRight: 0}}>Quadrigrams</a>
      </nav>

      {renderNGramList(unigramsToRender, '1-grams', 'uni')}
      {renderNGramList(bigramsToRender, '2-grams', 'bi')}
      {renderNGramList(trigramsToRender, '3-grams', 'tri')}
      {renderNGramList(quadrigramsToRender, '4-grams', 'quad')}
    </div>
  );
};

export default NGramDisplay;
